/**
 * O que cada área do menu exige (spec 058), nos dois layouts. Puro de propósito:
 * a guarda de navegação (apps/api/src/shared/simplified-nav.test.ts) o lê.
 *
 * A regra: a área aparece com a MESMA permissão que a API pede na tela dela.
 * As áreas que a API não barra para leitura (o Dashboard, a Agenda, os
 * Conhecimentos, o Cofre e as Configurações) têm, desde a spec 063, chave
 * própria (`dashboard.view`, `agenda.view`, `kb.view`, `vault.view`,
 * `settings.view`), ou a permissão de gestão da área. Até ali elas vinham com
 * `tickets.view_open`, e quem só cuida de uma mesa (o Estoque da PoolTrack) via
 * todas. Quem só atende conversas (um grupo com só `chat.access`) vê as
 * conversas, a conta e as notificações. Quem não está em grupo nenhum tem todas
 * as permissões, e nada muda para ele.
 */
import type { PermissionKey } from '@erp/shared';

/** As Configurações: a equipe de tickets, ou quem administra alguma coisa nelas. */
export const SETTINGS_ANY_OF = [
  'settings.view',
  'desks.manage',
  'users.manage',
  'permission_groups.manage',
  'integrations.manage',
  'catalogs.manage',
  'models.manage',
  'checklists.manage',
  'custom_fields.manage',
  'mail.manage',
  'scheduled.manage',
  'agendas.manage',
] as const satisfies readonly PermissionKey[];

export const NAV_ANY_OF = {
  dashboard: ['dashboard.view'],
  tickets: ['tickets.view_open', 'pretickets.access'],
  newTicket: ['tickets.view_open'],
  chats: ['chat.access'],
  clients: ['clients.view'],
  resources: ['resources.view'],
  contracts: ['contracts.view'],
  agenda: ['agenda.view', 'agenda.view_all', 'agendas.manage'],
  reports: ['reports.admin', 'reports.attendants'],
  knowledge: ['kb.view', 'kb.manage'],
  vault: ['vault.view', 'vault.manage'],
  management: ['dashboard.view'],
  settings: SETTINGS_ANY_OF,
} as const satisfies Record<string, readonly PermissionKey[]>;

/**
 * A primeira tela do layout Completo (spec 058): o Dashboard, para quem o vê;
 * sem ele, os Tickets (spec 063: quem só cuida de uma mesa), e quem só atende
 * conversas entra nelas. `permissions` ausente é o allow-all (o /me sem lista:
 * admin, ou quem não está em grupo nenhum). null = fica no Dashboard.
 */
export function classicHomeRedirect(permissions: readonly string[] | null | undefined): string | null {
  if (!permissions) return null;
  if (permissions.includes('dashboard.view')) return null;
  if (permissions.includes('tickets.view_open')) return '/tickets';
  return permissions.includes('chat.access') ? '/chats' : null;
}
